import React, { useEffect, useState } from "react";
import { adminCompanyApiService } from "../../../../infrastructure/api/admin/admin-company-api-service";
import type {
  CompanyOverview,
  CompanyOverviewRecentReport,
} from "../../../../domain/types/admin-company";

// ─── 알람/알림 카드 (백엔드 집계 미구현 → 클라이언트 상수로 유지) ─────────────
// 알람·알림 도메인이 정식 신설되면 이 두 상수는 API 응답으로 대체된다.
interface PlaceholderKpiCard {
  label: string;
  value: string;
  unit?: string;
  caption: string;
  tone: "primary" | "neutral" | "warn";
  trendGlyph: string;
}

const PLACEHOLDER_ALARM_CARD: PlaceholderKpiCard = {
  label: "미해결 알람",
  value: "0",
  unit: "건",
  caption: "최근 24시간",
  tone: "neutral",
  trendGlyph: "○",
};

const PLACEHOLDER_NOTIFICATION_CARD: PlaceholderKpiCard = {
  label: "알림",
  value: "0",
  unit: "건",
  caption: "확인 필요",
  tone: "warn",
  trendGlyph: "⚠",
};

// ─── tone helpers ────────────────────────────────────────────────────────────
const TONE_COLOR: Record<
  PlaceholderKpiCard["tone"],
  { value: string; glyph: string }
> = {
  primary: { value: "#4285F4", glyph: "#4285F4" },
  neutral: { value: "#252525", glyph: "#888888" },
  warn: { value: "#E0A800", glyph: "#E0A800" },
};

// KPI 1 / KPI 2 badge styles (mockup .badge-kpi1, .badge-kpi2)
const KPI_BADGE: Record<
  CompanyOverviewRecentReport["kpi"],
  { bg: string; color: string }
> = {
  "KPI 1": { bg: "#E8F4FC", color: "#1170BF" },
  "KPI 2": { bg: "#FFF4E5", color: "#B26A00" },
};

const KpiBadge: React.FC<{ kpi: CompanyOverviewRecentReport["kpi"] }> = ({
  kpi,
}) => {
  const c = KPI_BADGE[kpi];
  return (
    <span
      className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium flex-shrink-0"
      style={{ background: c.bg, color: c.color }}
    >
      {kpi}
    </span>
  );
};

// ─── status badge helpers ────────────────────────────────────────────────────
// MachineStatus enum → 라벨 + 색상.
const MACHINE_STATUS_LABEL: Record<string, { label: string; bg: string; color: string }> = {
  OFFLINE: { label: "● 오프라인", bg: "#F5F5F5", color: "#888" },
  WARMUP: { label: "● 예열 중", bg: "#FEF4E5", color: "#B26A00" },
  ONLINE: { label: "● 대기 중", bg: "#E6F4EA", color: "#1F7A3F" },
  WORKING: { label: "● 가동 중", bg: "#E8F4FC", color: "#1170BF" },
  WARNING: { label: "● 점검 필요", bg: "#FEF2F2", color: "#F24A4A" },
};

const formatStatusBadge = (status: string) =>
  MACHINE_STATUS_LABEL[status] ?? { label: `● ${status}`, bg: "#F5F5F5", color: "#888" };

// ─── date / number formatters ────────────────────────────────────────────────
const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
};

const formatTimeOnly = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mi}:${ss}`;
};

/** 가동 시간(초) → "N일 N시간 N분" 형태로 표기. */
const formatUptime = (seconds: number | null): string => {
  if (seconds == null || seconds <= 0) return "—";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}일`);
  if (hours > 0) parts.push(`${hours}시간`);
  // 일/시간이 모두 없을 때만 분을 단독 표기 (장시간 가동 시 분은 생략)
  if (days === 0 && (hours === 0 || minutes > 0)) parts.push(`${minutes}분`);
  return parts.join(" ");
};

// ─── shared placeholder fallback ─────────────────────────────────────────────
const EMPTY_OVERVIEW: CompanyOverview = {
  kpiCards: {
    materialSufficiencyRate: 0,
    materialSufficiencyCaption: "—",
    kpiCollectionRate: null,
    kpiCollectionCaption: "—",
  },
  representativeEquipment: null,
  recentReports: [],
  fleetStatus: { working: 0, idle: 0, maintenance: 0, offline: 0, total: 0 },
  dailyProcessing: {
    date: new Date().toISOString().slice(0, 10),
    intake: 0,
    inProgress: 0,
    completed: 0,
    failed: 0,
    processedRate: 0,
    failureRate: 0,
  },
};

// ─── shared fetch hook ───────────────────────────────────────────────────────
interface OverviewState {
  data: CompanyOverview | null;
  isLoading: boolean;
  errorMessage: string | null;
}

const useCompanyOverview = (companyId: number | null): OverviewState => {
  const [state, setState] = useState<OverviewState>({
    data: null,
    isLoading: false,
    errorMessage: null,
  });

  useEffect(() => {
    if (companyId === null || Number.isNaN(companyId)) {
      setState({ data: null, isLoading: false, errorMessage: null });
      return;
    }
    let cancelled = false;
    setState({ data: null, isLoading: true, errorMessage: null });
    (async () => {
      try {
        const response = await adminCompanyApiService.getCompanyOverview(
          companyId,
        );
        if (cancelled) return;
        setState({
          data: response.data ?? null,
          isLoading: false,
          errorMessage: null,
        });
      } catch (error) {
        if (cancelled) return;
        const message =
          error instanceof Error
            ? error.message
            : "회사 Overview 정보를 가져오지 못했습니다.";
        console.error("회사 Overview 조회 실패:", error);
        setState({ data: null, isLoading: false, errorMessage: message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return state;
};

interface OverviewTabProps {
  companyId: number | null;
  onNavigate?: (tab: "equipment-status") => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ companyId, onNavigate }) => {
  const { data, isLoading, errorMessage } = useCompanyOverview(companyId);
  const overview = data ?? EMPTY_OVERVIEW;
  const equipment = overview.representativeEquipment;
  const statusBadge = equipment ? formatStatusBadge(equipment.statusBadge) : null;

  // 처리율 / 실패율 막대 폭 (둘 다 0~100). 나머지는 중간(처리 중)을 회색으로 채운다.
  const completedWidth = `${overview.dailyProcessing.processedRate}%`;
  const failedWidth = `${overview.dailyProcessing.failureRate}%`;
  const inProgressWidth = `${Math.max(
    0,
    100 - overview.dailyProcessing.processedRate - overview.dailyProcessing.failureRate,
  )}%`;

  return (
    <div className="p-5">
      {isLoading && (
        <div className="mb-4 text-[12px] text-[#888]">불러오는 중…</div>
      )}
      {errorMessage && (
        <div
          className="mb-4 px-3 py-2 rounded-md text-[12px]"
          style={{
            background: "#EBF3FF",
            color: "#4285F4",
            border: "1px solid rgba(66,133,244,0.2)",
          }}
        >
          {errorMessage}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* 장비 연결 상태 */}
        <div className="border border-[#D9D9D9] rounded-[10px] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold">장비 연결 상태</h3>
            {statusBadge && (
              <span
                className="inline-flex items-center px-2 py-[2px] rounded text-[11px] font-medium"
                style={{ background: statusBadge.bg, color: statusBadge.color }}
              >
                {statusBadge.label}
              </span>
            )}
          </div>

          {equipment ? (
            <>
              <div className="flex items-center gap-4">
                <div
                  className="w-[140px] flex-shrink-0 flex items-center justify-center"
                  style={{
                    background: "#F2F3F6",
                    borderRadius: 6,
                    aspectRatio: "4 / 3",
                    fontSize: 48,
                    color: "#A8ADB8",
                  }}
                >
                  🖨️
                </div>
                <div className="flex-1">
                  <div className="text-[15px] font-semibold mb-0.5">
                    {equipment.modelName}
                  </div>
                  <div className="text-[11px] text-[#888] mb-3">
                    {equipment.modelSubtitle}
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11.5px]">
                    <div>
                      <span className="text-[#888]">IP 주소</span>
                      <span className="font-medium ml-1">
                        {equipment.ipAddress ?? "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#888]">MAC 주소</span>
                      <span className="font-medium ml-1">
                        {equipment.macAddress ?? "—"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#888]">최근 보고</span>
                      <span className="font-medium ml-1">
                        {formatTimeOnly(equipment.lastReportedAt)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[#888]">가동 시간</span>
                      <span className="font-medium ml-1">
                        {formatUptime(equipment.runtimeSeconds)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#D9D9D9] flex items-center justify-between text-[11px] text-[#888]">
                <span>KPI 보고는 대표 장비 1대에서만 전송됩니다.</span>
                <span
                  className="text-[#4285F4] cursor-pointer"
                  onClick={() => onNavigate?.("equipment-status")}
                >
                  장비 상세 ›
                </span>
              </div>
            </>
          ) : (
            <div className="py-8 text-center text-[12px] text-[#888]">
              등록된 장비가 없습니다.
            </div>
          )}
        </div>

        {/* 최근 보고 시각 */}
        <div className="border border-[#D9D9D9] rounded-[10px] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold">
              최근 보고 시각{" "}
              <span className="text-[#888] font-normal">(최근 5건)</span>
            </h3>
          </div>
          {overview.recentReports.length === 0 ? (
            <div className="py-8 text-center text-[12px] text-[#888]">
              최근 보고 이력이 없습니다.
            </div>
          ) : (
            <div className="space-y-0">
              {overview.recentReports.map((r, idx) => (
                <div
                  key={`${r.reportedAt}-${idx}`}
                  className={`flex items-center justify-between py-2.5 ${
                    idx < overview.recentReports.length - 1
                      ? "border-b border-[#D9D9D9]"
                      : ""
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <KpiBadge kpi={r.kpi} />
                    <span className="text-[11px] text-[#888] font-mono flex-shrink-0">
                      {r.classification}
                    </span>
                    <span className="text-[12.5px] truncate">{r.label}</span>
                  </div>
                  <span className="text-[11.5px] text-[#888] flex-shrink-0 ml-2">
                    {formatDateTime(r.reportedAt)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 실시간 장비 현황 */}
        <div className="border border-[#D9D9D9] rounded-[10px] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold">
              실시간 장비 현황{" "}
              <span className="text-[#888] font-normal">
                (전체 {overview.fleetStatus.total}대)
              </span>
            </h3>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">가동중</div>
              <div className="text-[22px] font-bold text-[#4285F4]">
                {overview.fleetStatus.working}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">대기</div>
              <div className="text-[22px] font-bold">
                {overview.fleetStatus.idle}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">정비</div>
              <div className="text-[22px] font-bold" style={{ color: "#E0A800" }}>
                {overview.fleetStatus.maintenance}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">오프라인</div>
              <div className="text-[22px] font-bold text-[#888]">
                {overview.fleetStatus.offline}
              </div>
            </div>
          </div>
        </div>

        {/* 장비 처리 현황 */}
        <div className="border border-[#D9D9D9] rounded-[10px] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[14px] font-semibold">
              장비 처리 현황{" "}
              <span className="text-[#888] font-normal">(오늘)</span>
            </h3>
            <span className="text-[11px] text-[#888]">
              {overview.dailyProcessing.date}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">접수</div>
              <div className="text-[22px] font-bold">
                {overview.dailyProcessing.intake}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">출력 중</div>
              <div className="text-[22px] font-bold text-[#4285F4]">
                {overview.dailyProcessing.inProgress}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">완료</div>
              <div className="text-[22px] font-bold" style={{ color: "#27AE60" }}>
                {overview.dailyProcessing.completed}
              </div>
            </div>
            <div className="bg-[#FAFAFA] rounded-md p-3 text-center">
              <div className="text-[11px] text-[#888] mb-1">실패</div>
              <div className="text-[22px] font-bold" style={{ color: "#F24A4A" }}>
                {overview.dailyProcessing.failed}
              </div>
            </div>
          </div>
          <div
            className="h-1.5 rounded-full overflow-hidden flex"
            style={{ background: "#FAFAFA" }}
          >
            <div style={{ background: "#27AE60", width: completedWidth }} />
            <div style={{ background: "#4285F4", width: inProgressWidth }} />
            <div style={{ background: "#F24A4A", width: failedWidth }} />
          </div>
          <div className="flex justify-between text-[10.5px] text-[#888] mt-1.5">
            <span>처리율 {overview.dailyProcessing.processedRate}%</span>
            <span>실패율 {overview.dailyProcessing.failureRate}%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── KPI 카드 4종 (페이지 헤더 영역) ─────────────────────────────────────────
// 자재 보유 충분율 / KPI 수집률 2종은 API 응답 사용. 미해결 알람 / 알림 2종은 mock 유지.

interface OverviewKpiCardsProps {
  companyId: number | null;
}

const formatRate = (value: number | null): string => {
  if (value === null || Number.isNaN(value)) return "—";
  // 소수점 1자리. 정수이면 정수만 표기.
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(1);
};

export const OverviewKpiCards: React.FC<OverviewKpiCardsProps> = ({
  companyId,
}) => {
  const { data } = useCompanyOverview(companyId);

  const materialValue = data
    ? formatRate(data.kpiCards.materialSufficiencyRate)
    : "—";
  const materialCaption = data?.kpiCards.materialSufficiencyCaption ?? "—";
  const kpiValue = data ? formatRate(data.kpiCards.kpiCollectionRate) : "—";
  const kpiCaption = data?.kpiCards.kpiCollectionCaption ?? "최근 7일 평균";

  const cards: PlaceholderKpiCard[] = [
    {
      label: "자재 보유 충분율",
      value: materialValue,
      unit: materialValue === "—" ? undefined : "%",
      caption: materialCaption,
      tone: "primary",
      trendGlyph: "↗",
    },
    {
      label: "KPI 수집률",
      value: kpiValue,
      unit: kpiValue === "—" ? undefined : "%",
      caption: kpiCaption,
      tone: "primary",
      trendGlyph: "↗",
    },
    PLACEHOLDER_ALARM_CARD,
    PLACEHOLDER_NOTIFICATION_CARD,
  ];

  return (
    <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
      {cards.map((card) => {
        const tone = TONE_COLOR[card.tone];
        return (
          <div
            key={card.label}
            className="border border-[#D9D9D9] rounded-[0.625rem] bg-white p-4"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[12px] text-[#555]">{card.label}</span>
              <span className="text-[14px]" style={{ color: tone.glyph }}>
                {card.trendGlyph}
              </span>
            </div>
            <div className="flex items-baseline gap-1">
              <span
                className="text-[28px] font-bold"
                style={{ color: tone.value }}
              >
                {card.value}
              </span>
              {card.unit && (
                <span className="text-[14px] text-[#888]">{card.unit}</span>
              )}
            </div>
            <div className="text-[11px] text-[#888] mt-1">{card.caption}</div>
          </div>
        );
      })}
    </section>
  );
};

export default OverviewTab;
